import * as THREE from 'three';
import type { CameraController } from './CameraController';

export interface IsometricFollowConfig {
  readonly offset: { readonly x: number; readonly y: number; readonly z: number };
  readonly positionLerp: number;
  readonly lookLerp: number;
  readonly fov?: number;
  readonly lookHeight?: number;
}

const UP_AXIS = new THREE.Vector3(0, 1, 0);

export class IsometricFollowController implements CameraController {
  private readonly baseOffset: THREE.Vector3;
  private readonly rotatedOffset = new THREE.Vector3();
  private readonly tmpDesired = new THREE.Vector3();
  private readonly tmpTargetLook = new THREE.Vector3();
  private readonly smoothedLook: THREE.Vector3;
  private readonly lookHeight: number;
  private yaw = 0;

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly target: THREE.Object3D,
    private readonly config: IsometricFollowConfig
  ) {
    if (config.fov !== undefined) {
      this.camera.fov = config.fov;
      this.camera.updateProjectionMatrix();
    }

    this.baseOffset = new THREE.Vector3(
      config.offset.x,
      config.offset.y,
      config.offset.z
    );
    this.lookHeight = config.lookHeight ?? 0;

    this.rotatedOffset.copy(this.baseOffset);
    this.tmpDesired.copy(target.position).add(this.rotatedOffset);
    this.camera.position.copy(this.tmpDesired);

    this.smoothedLook = target.position.clone();
    this.smoothedLook.y += this.lookHeight;
    this.camera.lookAt(this.smoothedLook);
  }

  addYaw(delta: number): void {
    this.yaw = wrapAngle(this.yaw + delta);
  }

  setYaw(value: number): void {
    this.yaw = wrapAngle(value);
  }

  getYaw(): number {
    return this.yaw;
  }

  update(dt: number): void {
    this.rotatedOffset.copy(this.baseOffset).applyAxisAngle(UP_AXIS, this.yaw);
    this.tmpDesired.copy(this.target.position).add(this.rotatedOffset);

    const posAlpha = 1 - Math.exp(-this.config.positionLerp * dt);
    this.camera.position.lerp(this.tmpDesired, posAlpha);

    this.tmpTargetLook.copy(this.target.position);
    this.tmpTargetLook.y += this.lookHeight;

    const lookAlpha = 1 - Math.exp(-this.config.lookLerp * dt);
    this.smoothedLook.lerp(this.tmpTargetLook, lookAlpha);
    this.camera.lookAt(this.smoothedLook);
  }
}

function wrapAngle(angle: number): number {
  let a = angle;
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}
